// src/hooks/useTranscription.ts
import { useState, useRef, useEffect, useContext } from "react";
import { RealtimeTranscriber, RealtimeTranscript } from "assemblyai";
import { getAssemblyAIToken } from "../api/assemblyAI";
import { convertFloat32ToInt16 } from "../utils/audioUtils";
import { AudioPlayingContext } from "../contexts/AudioPlayingContext";
import { httpsCallable } from "firebase/functions";
import { functions } from "../api/firebase";

interface UseTranscriptionProps {
    onFinalTranscript: (text: string) => void;
    playAudioByContent: (content: string) => void;
}

export const useTranscription = (props?: UseTranscriptionProps) => {
    const [isRecording, setIsRecording] = useState(false);
    const isTranscriptionPausedRef = useRef(!isRecording);
    const realtimeTranscriber = useRef<RealtimeTranscriber | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const audioWorkletNodeRef = useRef<AudioWorkletNode | null>(null);
    const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
    const onFinalTranscriptRef = useRef(props?.onFinalTranscript);
    const playAudioByContentRef = useRef(props?.playAudioByContent);
    const temporaryTranscriptRef = useRef<string | null>(null);
    const bufferLength = useRef(1); // init is 1
    const idleCountRef = useRef(0);

    const { isAudioPlayingRef, onAudioDisrubted, audioRef } =
        useContext(AudioPlayingContext)!;

    useEffect(() => {
        onFinalTranscriptRef.current = props?.onFinalTranscript;
    }, [props?.onFinalTranscript]);

    useEffect(() => {
        playAudioByContentRef.current = props?.playAudioByContent;
    }, [props?.playAudioByContent]);

    useEffect(() => {
        isTranscriptionPausedRef.current = !isRecording;
    }, [isRecording]);
    const getBufferedTranscription = () => {
        // If you also want to clear it out:
        const finalTranscript = temporaryTranscriptRef.current;
        const oldBufferLength = bufferLength.current;
        if (!finalTranscript) return { finalTranscript: "", bufferLength: 0 };
        setTimeout(() => {
            temporaryTranscriptRef.current = null;
            bufferLength.current = 1;
        }, 800);
        return { finalTranscript, bufferLength: oldBufferLength };
    };
    // Start the transcription session
    // handle audio disruptions & normal transcription
    const startTranscription = async (
        endUtteranceSilenceThreshold: number = 30_000,
        wordBoosts?: string[]
    ) => {
        console.log("CALLED START TRANSCRIPTION");
        // Prevent multiple initializations
        if (realtimeTranscriber.current) return;

        // First verify the processor.js file is accessible
        try {
            console.log("Verifying processor.js is accessible...");
            const response = await fetch("/processor.js");
            if (!response.ok) {
                throw new Error(
                    `Failed to fetch processor.js: ${response.status} ${response.statusText}`
                );
            }
            const text = await response.text();
            console.log(
                "Successfully verified processor.js exists and is accessible"
            );
            if (
                !text.includes(
                    "class MyAudioProcessor extends AudioWorkletProcessor"
                )
            ) {
                console.warn("Warning: processor.js content may be incorrect");
            }
        } catch (error) {
            console.error("Error checking processor.js:", error);
        }

        setIsRecording(true);
        const passedInWordBoosts = wordBoosts || [];
        console.log("Passed in word boosts:", passedInWordBoosts);
        // Initialize RealtimeTranscriber
        realtimeTranscriber.current = new RealtimeTranscriber({
            token: await getAssemblyAIToken(),
            endUtteranceSilenceThreshold: endUtteranceSilenceThreshold,
            sampleRate: 16000,
            wordBoost: [
                "Profit margin",
                "sales share",
                "competitive advantage",
                "competitor analysis",
                "strategy",
                "stakeholders",
                "deliverables",
                "implementation",
                "optimization",
                "KPI",
                "benchmarking",
                "value proposition",
                "scalability",
                "best practices",
                "core competencies",
                "change management",
                "due diligence",
                "synergy",
                "business intelligence",
                "client-facing",
                "market segmentation",
                "assets",
                "liabilities",
                "equity",
                "revenue",
                "expenses",
                "depreciation",
                "accounts receivable",
                "accounts payable",
                "cash flow",
                "balance sheet",
                "income statement",
                "accrual",
                "amortization",
                "EBITDA",
                "working capital",
                "gross margin",
                "operating income",
                "retained earnings",
                "ROI",
                "ROE",
                "CaGR",
                "overhead costs",
                "capital expenditure",
                "operational expenditure",
                "leverage",
                "liquidity ratio",
                "profit margin",
                "market share",
                "CapEx",

                "market penetration",
                "accessories",
                ...passedInWordBoosts,
            ],
        });

        const transcriber = realtimeTranscriber.current;
        transcriber.on("open", () => {
            console.log("Transcription session opened");
        });
        // Set up event listeners
        // let discardUntilTime: number | null = null;
        transcriber.on("transcript", (transcript: RealtimeTranscript) => {
            // const currentTime = Date.now();

            // if (discardUntilTime && currentTime < discardUntilTime) {
            //     console.log("Discarding transcript due to resume delay");
            //     return; // Discard any transcripts right after resuming
            // }
            // if (
            //     !transcript.text ||
            //     transcript.text.length < 5 ||
            //     isTranscriptionPausedRef.current
            // )
            //     return;
            // if (
            //     isAudioPlayingRef.current &&
            //     audioRef.current &&
            //     audioRef.current.duration > 2
            // ) {
            //     transcript.text = "";
            //     pauseAndResumeTranscription(audioRef.current.duration * 1_000);
            //     discardUntilTime = currentTime + 3_000; // Discard transcriptions for 3 seconds after resuming
            //     onAudioDisrubted();
            //     return;
            // }
            if (transcript.message_type === "PartialTranscript") {
                // console.log("Partial:", transcript.text);
            }
        });

        transcriber.on("transcript.final", (transcript: RealtimeTranscript) => {
            // this is to buffer the transcript for webscocket limit
            if (!temporaryTranscriptRef.current) {
                temporaryTranscriptRef.current = transcript.text;
            } else {
                temporaryTranscriptRef.current += " " + transcript.text;
            }
            bufferLength.current++;
            console.log("Accumulating final transcript:", transcript.text);
            console.log("[buffered]", temporaryTranscriptRef.current);
            // now the recording is paused (only triggered by clicking finish talking...)
            // if (onFinalTranscriptRef.current) {
            //     if (
            //         playAudioByContentRef.current &&
            //         temporaryTranscriptRef.current &&
            //         temporaryTranscriptRef.current.length > 0
            //     ) {
            //         playAudioByContentRef.current(
            //             "give me a couple seconds, i'll let u know when we resume"
            //         );
            //     }
            //     let tempText = "";
            //     if (
            //         temporaryTranscriptRef.current &&
            //         temporaryTranscriptRef.current.length > 0
            //     ) {
            //         tempText = temporaryTranscriptRef.current;
            //         console.log("temp text", tempText);
            //     }
            //     const finalText = tempText + transcript.text;
            //     console.log("!!!", finalText);
            //     onFinalTranscriptRef.current(finalText);
            //     temporaryTranscriptRef.current = null;
            // }
        });

        transcriber.on("error", (error) => {
            console.error("Transcription error:", error);
            // Check if the error is related to idleness
            if (
                typeof error.message === "string" &&
                error.message.includes("idle")
            ) {
                if (idleCountRef.current < 3) {
                    idleCountRef.current++;
                    console.log(
                        `Idle error encountered (${idleCountRef.current}/3). Restarting transcription.`
                    );
                    forceEndUtteranceTranscription().then(() => {
                        // Optionally, you can reset any other state here before restarting.
                        startTranscription();
                    });
                    if (idleCountRef.current === 2) {
                        playAudioByContentRef.current?.(
                            "Hey there, how's your thought process going. It's generally a good practice to keep talking to let interviewer know about your thought process, even if you're stuck. I'll give you one more minute to gather your thoughts."
                        );
                    }
                } else {
                    console.warn(
                        "Maximum idle attempts reached. Closing transcription."
                    );
                    handleIdleError();
                    closeTranscription();
                }
            } else {
                closeTranscription();
            }
        });

        transcriber.on("close", (code, reason) => {
            console.log("Transcription closed:", code, reason);
            closeTranscription();
        });

        await transcriber.connect();

        // Set up AudioContext and Worklet
        try {
            console.log("Attempting to get user media stream...");
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: true,
            });
            console.log("Successfully obtained media stream");

            console.log("Creating AudioContext with 16000 sample rate...");
            const audioContext = new AudioContext({ sampleRate: 16000 });
            console.log(
                "AudioContext created successfully:",
                audioContext.state
            );

            // Get the base URL from the homepage setting in package.json
            const basePath = process.env.PUBLIC_URL || "/dashboard";
            console.log("Using base path:", basePath);

            // Try with base path first
            const processorUrl = `${basePath}/processor.js`;
            console.log("Attempting to load worklet from:", processorUrl);
            try {
                await audioContext.audioWorklet.addModule(processorUrl);
                console.log("Audio worklet module loaded successfully");
            } catch (workletError) {
                console.error(
                    "Failed to load with base path, trying absolute path..."
                );
                // If base path fails, try absolute path
                try {
                    const absoluteUrl = new URL(
                        "/processor.js",
                        window.location.origin
                    ).href;
                    console.log("Attempting with absolute URL:", absoluteUrl);
                    await audioContext.audioWorklet.addModule(absoluteUrl);
                    console.log(
                        "Audio worklet module loaded successfully with absolute path"
                    );
                } catch (absoluteError) {
                    console.error(
                        "Failed with absolute path, trying relative..."
                    );
                    // If absolute path fails, try relative as last resort
                    try {
                        await audioContext.audioWorklet.addModule(
                            "./processor.js"
                        );
                        console.log(
                            "Audio worklet module loaded successfully with relative path"
                        );
                    } catch (fallbackError) {
                        console.error("All path attempts failed:", {
                            basePath: workletError,
                            absolutePath: absoluteError,
                            relativePath: fallbackError,
                        });
                        throw fallbackError;
                    }
                }
            }

            console.log("Creating media stream source...");
            const source = audioContext.createMediaStreamSource(stream);
            console.log("Media stream source created successfully");

            console.log("Creating AudioWorkletNode...");
            const audioWorkletNode = new AudioWorkletNode(
                audioContext,
                "processor"
            );
            console.log("AudioWorkletNode created successfully");

            console.log("Connecting audio nodes...");
            source.connect(audioWorkletNode);
            console.log("Audio nodes connected successfully");

            audioWorkletNode.port.onmessage = (event) => {
                const audioData = event.data; // Float32Array
                if (!isTranscriptionPausedRef.current) {
                    const int16Data = convertFloat32ToInt16(audioData);
                    const buffer = int16Data.buffer;
                    transcriber.sendAudio(buffer);
                }
            };
            // Store references for cleanup
            audioContextRef.current = audioContext;
            audioWorkletNodeRef.current = audioWorkletNode;
            sourceRef.current = source;
            console.log("All audio components initialized successfully");
        } catch (error: unknown) {
            console.error("Error in audio setup:", error);
            if (error instanceof Error) {
                console.error("Error details:", {
                    name: error.name,
                    message: error.message,
                    stack: error.stack,
                });
            }
            closeTranscription();
            return;
        }
    };

    const { stopAudio } = useContext(AudioPlayingContext)!;
    const closeTranscription = async () => {
        console.log("CALLED CLOSE TRANSCRIPTION");
        setIsRecording(false);
        stopAudio();
        if (realtimeTranscriber.current) {
            console.log("Closing transcription session");
            await realtimeTranscriber.current.close(false);
            realtimeTranscriber.current = null;
        }

        console.log("Closing audio context");
        // Cleanup audio context and nodes
        audioWorkletNodeRef.current?.port.close();
        audioWorkletNodeRef.current?.disconnect();
        sourceRef.current?.disconnect();
        audioContextRef.current?.close();

        // Reset refs
        audioWorkletNodeRef.current = null;
        sourceRef.current = null;
        audioContextRef.current = null;
        temporaryTranscriptRef.current = null;
    };

    const forceEndUtteranceTranscription = async () => {
        console.log("temporary buffer::", temporaryTranscriptRef.current);

        // await new Promise((resolve) => setTimeout(resolve, 1200)); // this seems not tracking everything tho...
        console.log("temporary buffer:::::", temporaryTranscriptRef.current);
        isTranscriptionPausedRef.current = true; // Update the ref`
        setIsRecording(false);
        if (audioWorkletNodeRef.current) {
            audioWorkletNodeRef.current.port.postMessage("pause");
            realtimeTranscriber.current?.forceEndUtterance();
            console.log("...Forcing end of utterance...");
            console.log("temporary buffer!!!!", temporaryTranscriptRef.current);

            let isThisCallResolved = false;
            console.log("Is called resolved?", isThisCallResolved);
            return new Promise<void>((resolve) => {
                const safeResolve = (reason: string) => {
                    if (!isThisCallResolved) {
                        isThisCallResolved = true;
                        console.log(
                            `Resolving forceEndUtteranceTranscription. Reason: ${reason}`
                        );
                        resolve();
                    }
                };
                const handleFinal = async (transcript: RealtimeTranscript) => {
                    // If we haven't resolved *this* call yet, do it
                    isThisCallResolved = false;
                    console.log(
                        "In side final, is called resolved?",
                        isThisCallResolved
                    );
                    if (!isThisCallResolved) {
                        console.log(
                            "Call not resolved, resolving now. Transcript: ",
                            transcript.text,
                            "Current Buffer:",
                            temporaryTranscriptRef.current
                        );
                        isThisCallResolved = true;
                        if (!temporaryTranscriptRef.current) {
                            temporaryTranscriptRef.current = "";
                        }
                        bufferLength.current++;

                        temporaryTranscriptRef.current += " " + transcript.text;

                        resolve();
                        return;
                    }

                    // console.log("End of web socket cycle?", transcript.text);
                    // console.log(
                    //     "[END OF WS] Buffer:",
                    //     temporaryTranscriptRef.current
                    // );
                    // console.log(
                    //     "[END OF WS] Buffer length:",
                    //     bufferLength.current
                    // );
                    // if (!temporaryTranscriptRef.current) {
                    //     temporaryTranscriptRef.current = transcript.text;
                    // } else {
                    //     temporaryTranscriptRef.current += " " + transcript.text;
                    // }
                };

                console.log("before handle Final", isThisCallResolved);
                // Attach the callback
                realtimeTranscriber?.current?.on(
                    "transcript.final",
                    handleFinal
                );
                realtimeTranscriber?.current?.forceEndUtterance();
                setTimeout(() => {
                    console.log(
                        "No final transcript arrived in 800ms, resolving anyway..."
                    );
                    safeResolve("Timeout fallback");
                }, 800);
            });
        }
    };

    const pauseTranscription = () => {
        isTranscriptionPausedRef.current = true; // Update the ref
        setIsRecording(false);
        if (audioWorkletNodeRef.current) {
            audioWorkletNodeRef.current.port.postMessage("pause");
        }
    };

    // maybe add throttle to this
    const resumeTranscription = () => {
        setIsRecording(true);
        isTranscriptionPausedRef.current = false; // Update the ref
        if (audioWorkletNodeRef.current) {
            audioWorkletNodeRef.current.port.postMessage("resume");
            realtimeTranscriber.current?.configureEndUtteranceSilenceThreshold(
                10_000
            );
        }
        console.log("Transcription resumed");
    };
    const setOnFinalTranscript = (callback: (text: string) => void) => {
        onFinalTranscriptRef.current = callback;
    };

    const handleIdleError = async () => {
        const handleIdleInterview = httpsCallable(
            functions,
            "handleIdleInterview"
        );
        await handleIdleInterview();
    };

    return {
        isRecording,
        startTranscription,
        closeTranscription,
        pauseTranscription,
        setOnFinalTranscript,
        getBufferedTranscription,
        forcePauseTranscription: forceEndUtteranceTranscription,
        resumeTranscription,
    };
};
